import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  PlusIcon,
  BuildingIcon,
  MapPinIcon,
  FileTextIcon,
  ClipboardIcon,
  UsersIcon,
  SaveIcon,
  CalendarIcon,
  StickyNoteIcon,
  HistoryIcon,
  ClipboardListIcon,
  UserCheckIcon,
  EditIcon,
  MoreHorizontalIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import {
  LEAD_STAGES,
  formatLeadStage,
  getContactDisplayName,
} from '../lib/leadHelpers'
import { getEntityActivities, ActivityLog, logActivity } from '../utils/activityLogger'
import { NotesAndActivityPanel } from '../components/NotesAndActivityPanel'
import { ConversationsPanel } from '../components/ConversationsPanel'
import { ConvertLeadModal } from '../components/ConvertLeadModal'
import { MobilePage, MobilePageHeader, MobileSheet, mobile } from '../components/mobile'
import { ViewTabBar } from '../components/ViewPageLayout'

interface LeadNote {
  id: string
  note: string
  created_at: string
  created_by_name?: string
  note_type?: string
}

interface Project {
  id?: string
  type?: string
  address: string
  address_line_2?: string
  city?: string
  state?: string
  zip?: string
  unit_count?: string
  work_type?: string
  notes?: string
}

export default function LeadView() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const tabFromUrl = searchParams.get('tab') as 'overview' | 'contacts' | 'projects' | 'proposals' | 'notes' | null
  const highlightProjectId = searchParams.get('highlightProject')
  const [activeTab, setActiveTab] = useState<
    'overview' | 'contacts' | 'projects' | 'proposals' | 'notes'
  >(tabFromUrl || 'overview')

  const [lead, setLead] = useState<any>(null)
  const [projects, setProjects] = useState<Project[]>([])
  const [notes, setNotes] = useState<LeadNote[]>([])
  const [contacts, setContacts] = useState<any[]>([])
  const [proposals, setProposals] = useState<any[]>([])
  const [proposalRequests, setProposalRequests] = useState<any[]>([])
  const [leadSources, setLeadSources] = useState<any[]>([])
  const [users, setUsers] = useState<any[]>([])
  const [serviceCategories, setServiceCategories] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [newNote, setNewNote] = useState('')
  const [savingNote, setSavingNote] = useState(false)
  const [activities, setActivities] = useState<ActivityLog[]>([])
  const [isEditingNextActivityDate, setIsEditingNextActivityDate] = useState(false)
  const [nextActivityDateValue, setNextActivityDateValue] = useState<string>('')
  const [savingNextActivityDate, setSavingNextActivityDate] = useState(false)
  const [showConvertModal, setShowConvertModal] = useState(false)
  const [actionsOpen, setActionsOpen] = useState(false)
  const [notification, setNotification] = useState<{
    message: string
    type: 'success' | 'error'
    customerId?: string
  } | null>(null)

  useEffect(() => {
    if (id) {
      fetchLeadData()
      fetchActivities()
      fetchProposals()
      fetchProposalRequests()
      fetchLeadSources()
      fetchUsers()
      fetchServiceCategories()
    }
  }, [id])

  // Set active tab from URL params
  useEffect(() => {
    if (tabFromUrl) {
      setActiveTab(tabFromUrl)
    }
  }, [tabFromUrl])

  useEffect(() => {
    if (activeTab !== 'projects' || !highlightProjectId) return
    const el = document.getElementById(`lead-project-${highlightProjectId}`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [activeTab, highlightProjectId, projects])

  const fetchActivities = async () => {
    if (!id) return
    try {
      const acts = await getEntityActivities('lead', id)
      setActivities(acts)
    } catch (error) {
      console.error('Error fetching activities:', error)
    }
  }

  const fetchProposals = async () => {
    if (!id) return
    try {
      // Fetch proposals linked to this lead (only actual proposals, not requests)
      const { data, error } = await supabase
        .from('proposals')
        .select('*')
        .eq('lead_id', id)
        .eq('record_type', 'proposal')
        .order('created_at', { ascending: false })

      if (error) throw error

      setProposals(data || [])
    } catch (error) {
      console.error('Error fetching proposals:', error)
    }
  }

  const fetchProposalRequests = async () => {
    if (!id) return
    try {
      const { data, error } = await supabase
        .from('proposals')
        .select('*')
        .eq('lead_id', id)
        .eq('record_type', 'request')
        .order('created_at', { ascending: false })

      if (error) throw error
      setProposalRequests(data || [])
    } catch (error) {
      console.error('Error fetching proposal requests:', error)
    }
  }

  const fetchLeadSources = async () => {
    try {
      const { data, error } = await supabase
        .from('lead_sources')
        .select('id, name')
        .eq('is_active', true)
        .order('name')

      if (error) throw error
      setLeadSources(data || [])
    } catch (error) {
      console.error('Error fetching lead sources:', error)
    }
  }

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, email')
        .order('first_name')
      if (error) throw error
      setUsers(data || [])
    } catch (error) {
      console.error('Error fetching users:', error)
    }
  }

  const fetchServiceCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('service_categories')
        .select('id, name')
        .order('name')

      if (error) throw error
      setServiceCategories(data || [])
    } catch (error) {
      console.error('Error fetching service categories:', error)
    }
  }

  const getServiceCategoryName = (categoryId: string) => {
    const category = serviceCategories.find((cat) => cat.id === categoryId)
    return category ? category.name : categoryId
  }

  const fetchLeadData = async () => {
    try {
      // Fetch lead
      const { data: leadData, error: leadError } = await supabase
        .from('leads')
        .select('*')
        .eq('id', id)
        .single()

      if (leadError) throw leadError
      setLead(leadData)

      // Fetch notes from lead_notes table
      const { data: notesData, error: notesError } = await supabase
        .from('lead_notes')
        .select('id, note, created_at, created_by_name, note_type')
        .eq('lead_id', id)
        .order('created_at', { ascending: false })

      if (!notesError && notesData) {
        setNotes(notesData)
      }

      // Fetch contacts if available
      if (leadData.contacts && Array.isArray(leadData.contacts)) {
        setContacts(leadData.contacts)
      }

      // Fetch projects if available
      if (leadData.projects && Array.isArray(leadData.projects)) {
        setProjects(leadData.projects)
      }

      // Set next activity date value
      if (leadData.next_activity_date) {
        const date = new Date(leadData.next_activity_date + 'T00:00:00')
        const formattedDate = date.toISOString().split('T')[0]
        setNextActivityDateValue(formattedDate)
      }
    } catch (error) {
      console.error('Error fetching lead data:', error)
      alert('Error loading lead: ' + (error as any).message)
    } finally {
      setLoading(false)
    }
  }

  const handleAddNote = async () => {
    if (!newNote.trim() || !id) return

    setSavingNote(true)
    try {
      const { error } = await supabase
        .from('lead_notes')
        .insert({
          lead_id: id,
          note: newNote.trim(),
          note_type: 'general',
        })

      if (error) throw error

      // Refresh notes
      const { data: notesData } = await supabase
        .from('lead_notes')
        .select('id, note, created_at, created_by_name, note_type')
        .eq('lead_id', id)
        .order('created_at', { ascending: false })

      if (notesData) {
        setNotes(notesData)
      }

      setNewNote('')

      // Log activity
      await logActivity({
        activity_type: 'note_added',
        entity_type: 'lead',
        entity_id: id!,
        description: 'Note added to lead',
      })

      fetchActivities()
    } catch (error) {
      console.error('Error adding note:', error)
      alert('Error adding note')
    } finally {
      setSavingNote(false)
    }
  }

  const updateLeadField = async (
    fields: Record<string, unknown>,
    description: string
  ) => {
    if (!lead?.id) return
    try {
      const { error } = await supabase.from('leads').update(fields).eq('id', lead.id)
      if (error) throw error
      setLead({ ...lead, ...fields })
      await logActivity({
        activity_type: 'status_changed',
        entity_type: 'lead',
        entity_id: lead.id,
        description,
        metadata: fields,
      })
      fetchActivities()
    } catch (error) {
      console.error('Error updating lead:', error)
      alert('Error saving changes')
    }
  }

  const handleSaveNextActivityDate = async () => {
    if (!lead?.id) return

    setSavingNextActivityDate(true)
    try {
      const { error } = await supabase
        .from('leads')
        .update({ next_activity_date: nextActivityDateValue || null })
        .eq('id', lead.id)

      if (error) throw error

      // Update local state
      setLead({ ...lead, next_activity_date: nextActivityDateValue || null })
      setIsEditingNextActivityDate(false)

      // Log activity
      await logActivity({
        activity_type: 'status_changed',
        entity_type: 'lead',
        entity_id: lead.id,
        description: `Next activity date updated to ${nextActivityDateValue || 'none'}`,
        metadata: { next_activity_date: nextActivityDateValue },
      })

      fetchActivities()
    } catch (error) {
      console.error('Error updating next activity date:', error)
      alert('Error updating next activity date')
    } finally {
      setSavingNextActivityDate(false)
    }
  }

  const handleCancelEditNextActivityDate = () => {
    if (lead?.next_activity_date) {
      const date = new Date(lead.next_activity_date + 'T00:00:00')
      const formattedDate = date.toISOString().split('T')[0]
      setNextActivityDateValue(formattedDate)
    } else {
      setNextActivityDateValue('')
    }
    setIsEditingNextActivityDate(false)
  }

  if (loading) {
    return (
      <MobilePage className="flex items-center justify-center min-h-[400px]">
        <p className="text-gray-600">Loading...</p>
      </MobilePage>
    )
  }

  if (!lead) {
    return (
      <MobilePage className="flex items-center justify-center min-h-[400px]">
        <p className="text-gray-600">Lead not found</p>
      </MobilePage>
    )
  }

  const leadSource = leadSources.find((source) => source.id === lead.lead_source_id)

  const formatDateCompact = (dateString: string | null | undefined) => {
    if (!dateString) return 'Not set'
    try {
      const date = new Date(dateString + 'T00:00:00')
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  const handleBack = () => {
    if (returnTo) {
      navigate(decodeURIComponent(returnTo))
    } else {
      navigate('/leads')
    }
  }
  const backLabel = returnTo
    ? (() => {
        const decoded = decodeURIComponent(returnTo)
        if (decoded.includes('/proposals/view')) return 'Back to Proposal'
        if (decoded.includes('/contracts/view') || decoded.includes('/contracts/details'))
          return 'Back to Contract'
        if (decoded.includes('/jobs/details')) return 'Back to Job'
        if (decoded.includes('/invoices/')) return 'Back to Invoice'
        return 'Back'
      })()
    : 'Back to Leads'

  const leadTitle =
    lead.company_name || `${lead.contact_first_name || ''} ${lead.contact_last_name || ''}`.trim() || 'Lead'

  return (
    <MobilePage className="max-w-6xl mx-auto pb-28 lg:pb-8">
      <MobilePageHeader
        sticky
        title={leadTitle}
        subtitle={lead.stage ? formatLeadStage(lead.stage) : undefined}
        onBack={handleBack}
        backLabel={backLabel}
        actions={
          <button
            type="button"
            onClick={() => setActionsOpen(true)}
            className={`${mobile.secondaryBtn} !min-h-[40px] !px-3 text-xs sm:text-sm`}
            aria-label="More actions"
          >
            <MoreHorizontalIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Actions</span>
          </button>
        }
      />

      <div className={`${mobile.surface} flex flex-col min-h-0`}>
        <ViewTabBar
          sticky
          tabs={[
            { id: 'overview', label: 'Overview' },
            { id: 'contacts', label: 'Contacts' },
            { id: 'projects', label: 'Projects' },
            { id: 'proposals', label: 'Proposals' },
            { id: 'notes', label: 'Notes', full: 'Notes & Activity' },
          ]}
          activeTab={activeTab}
          onChange={setActiveTab}
        />

          <div className="p-3 sm:p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPinIcon className="w-5 h-5 text-purple-600" />
                      <h3 className="font-semibold text-gray-900">Projects</h3>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{projects.length}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <FileTextIcon className="w-5 h-5 text-orange-600" />
                      <h3 className="font-semibold text-gray-900">Proposals</h3>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{proposals.length}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <UsersIcon className="w-5 h-5 text-green-600" />
                      <h3 className="font-semibold text-gray-900">Contacts</h3>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{contacts.length}</p>
                  </div>
                </div>

                {id && <ConversationsPanel entityType="lead" entityId={id} />}

                <div className="bg-blue-50 rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <BuildingIcon className="w-6 h-6 text-blue-600" />
                    <h2 className="text-xl font-semibold text-gray-900">Company Information</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Company Name
                      </label>
                      <p className="mt-1 text-sm text-gray-900">{lead.company_name || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Company Email
                      </label>
                      <p className="mt-1 text-sm text-gray-900">{lead.company_email || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Company Phone
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {lead.company_phone ? (
                          <a
                            href={`tel:${lead.company_phone.replace(/\D/g, '')}`}
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            {lead.company_phone}
                          </a>
                        ) : (
                          'N/A'
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Company Address
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {lead.company_address || 'N/A'}
                        {lead.company_address_line_2 && (
                          <>
                            <br />
                            {lead.company_address_line_2}
                          </>
                        )}
                        {lead.company_city && `, ${lead.company_city}`}
                        {lead.company_state && `, ${lead.company_state}`}
                        {lead.company_zip && ` ${lead.company_zip}`}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Company Website
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {lead.company_website ? (
                          <a
                            href={
                              lead.company_website.startsWith('http')
                                ? lead.company_website
                                : `https://${lead.company_website}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            {lead.company_website}
                          </a>
                        ) : (
                          'N/A'
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-teal-50 rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <ClipboardListIcon className="w-6 h-6 text-teal-600" />
                    <h2 className="text-xl font-semibold text-gray-900">Lead Management</h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg border border-teal-100 p-4 shadow-sm">
                      <label className="block text-sm font-medium text-gray-700">
                        Lead Source
                      </label>
                      <p className="mt-1 text-sm font-medium text-gray-900">
                        {leadSource?.name || 'Not set'}
                      </p>
                      {lead.lead_source_other && (
                        <p className="text-xs text-gray-600 mt-1">{lead.lead_source_other}</p>
                      )}
                    </div>

                    <div className="bg-white rounded-lg border border-teal-100 p-4 shadow-sm">
                      <label className="block text-sm font-medium text-gray-700">Stage</label>
                      <select
                        value={lead.stage || 'new'}
                        onChange={(e) =>
                          updateLeadField(
                            { stage: e.target.value },
                            `Stage updated to ${formatLeadStage(e.target.value)}`
                          )
                        }
                        className="mt-2 block w-full text-sm text-gray-900 border border-gray-200 rounded-md px-2 py-1.5 bg-white hover:bg-gray-50 focus:ring-1 focus:ring-teal-500 focus:border-teal-500 cursor-pointer"
                      >
                        {LEAD_STAGES.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="bg-white rounded-lg border border-teal-100 p-4 shadow-sm">
                      <label className="block text-sm font-medium text-gray-700">
                        Assigned To
                      </label>
                      <select
                        value={lead.assigned_to || ''}
                        onChange={(e) => {
                          const userId = e.target.value || null
                          const user = users.find((u) => u.id === userId)
                          updateLeadField(
                            { assigned_to: userId },
                            userId
                              ? `Assigned to ${user?.first_name || ''} ${user?.last_name || ''}`.trim()
                              : 'Lead unassigned'
                          )
                        }}
                        className="mt-2 block w-full text-sm text-gray-900 border border-gray-200 rounded-md px-2 py-1.5 bg-white hover:bg-gray-50 focus:ring-1 focus:ring-teal-500 focus:border-teal-500 cursor-pointer"
                      >
                        <option value="">Unassigned</option>
                        {users.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.first_name} {user.last_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="bg-white rounded-lg border border-teal-100 p-4 shadow-sm">
                      <label className="block text-sm font-medium text-gray-700">
                        Next Activity
                      </label>
                      {isEditingNextActivityDate ? (
                        <div className="mt-2 space-y-2">
                          <input
                            type="date"
                            value={nextActivityDateValue}
                            onChange={(e) => setNextActivityDateValue(e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                            disabled={savingNextActivityDate}
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={handleSaveNextActivityDate}
                              disabled={savingNextActivityDate}
                              className="flex-1 px-2 py-1 bg-teal-600 text-white text-xs rounded-md hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                            >
                              {savingNextActivityDate ? (
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                              ) : (
                                <>
                                  <SaveIcon className="w-3 h-3" />
                                  Save
                                </>
                              )}
                            </button>
                            <button
                              onClick={handleCancelEditNextActivityDate}
                              disabled={savingNextActivityDate}
                              className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-2 flex items-start gap-2">
                          <CalendarIcon className="w-4 h-4 text-teal-500 mt-0.5 shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900">
                              {formatDateCompact(lead.next_activity_date)}
                            </p>
                            <button
                              onClick={() => setIsEditingNextActivityDate(true)}
                              className="text-teal-600 hover:text-teal-800 text-xs mt-0.5"
                            >
                              Edit date
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Contacts Tab */}
            {activeTab === 'contacts' && (
              <div className="space-y-6">
                <div className="bg-green-50 rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <UsersIcon className="w-6 h-6 text-green-600" />
                    <h2 className="text-xl font-semibold text-gray-900">
                      Contacts ({contacts.length})
                    </h2>
                  </div>
                  {contacts.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {contacts.map((contact, index) => (
                        <div
                          key={contact.id || index}
                          className="bg-white rounded-lg border border-green-100 p-4 shadow-sm"
                        >
                          <h3 className="font-semibold text-gray-900 mb-1">
                            {getContactDisplayName(contact)}
                          </h3>
                          {contact.title && (
                            <p className="text-sm text-gray-600 mb-3">{contact.title}</p>
                          )}
                          <div className="space-y-2 text-sm">
                            {contact.phone && (
                              <div>
                                <label className="block text-xs font-medium text-gray-500">
                                  Phone
                                </label>
                                <a
                                  href={`tel:${contact.phone.replace(/\D/g, '')}`}
                                  className="text-blue-600 hover:text-blue-800 underline"
                                >
                                  {contact.phone}
                                  {contact.extension && ` (Ext: ${contact.extension})`}
                                </a>
                              </div>
                            )}
                            {contact.cell && (
                              <div>
                                <label className="block text-xs font-medium text-gray-500">
                                  Cell
                                </label>
                                <a
                                  href={`tel:${contact.cell.replace(/\D/g, '')}`}
                                  className="text-blue-600 hover:text-blue-800 underline"
                                >
                                  {contact.cell}
                                </a>
                              </div>
                            )}
                            {contact.email && (
                              <div>
                                <label className="block text-xs font-medium text-gray-500">
                                  Email
                                </label>
                                <a
                                  href={`mailto:${contact.email}`}
                                  className="text-blue-600 hover:text-blue-800 underline break-all"
                                >
                                  {contact.email}
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg border border-dashed border-green-200 p-8 text-center">
                      <UsersIcon className="w-10 h-10 text-green-300 mx-auto mb-3" />
                      <p className="text-gray-500">No contacts found</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Projects Tab */}
            {activeTab === 'projects' && (
              <div className="space-y-6">
                <div className="bg-purple-50 rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <MapPinIcon className="w-6 h-6 text-purple-600" />
                    <h2 className="text-xl font-semibold text-gray-900">
                      Projects ({projects.length})
                    </h2>
                  </div>
                  {projects.length > 0 ? (
                    <div className="space-y-4">
                      {projects.map((project, index) => (
                        <div
                          key={project.id || index}
                          id={project.id ? `lead-project-${project.id}` : undefined}
                          className={`bg-white rounded-lg border p-5 shadow-sm ${
                            highlightProjectId && project.id === highlightProjectId
                              ? 'border-purple-500 ring-2 ring-purple-200'
                              : 'border-purple-100'
                          }`}
                        >
                          <h3 className="text-lg font-semibold text-gray-900">
                            {project.type || 'Project'}
                            {project.unit_count && (
                              <span className="text-sm font-normal text-gray-600">
                                {' '}
                                ({project.unit_count} units)
                              </span>
                            )}
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                Address
                              </label>
                              <p className="mt-1 text-sm text-gray-900">
                                {project.address || 'N/A'}
                                {project.address_line_2 && (
                                  <>
                                    <br />
                                    {project.address_line_2}
                                  </>
                                )}
                                {project.city && `, ${project.city}`}
                                {project.state && `, ${project.state}`}
                                {project.zip && ` ${project.zip}`}
                              </p>
                            </div>
                            {project.work_type && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700">
                                  Work Type
                                </label>
                                <p className="mt-1 text-sm text-gray-900">
                                  {getServiceCategoryName(project.work_type)}
                                </p>
                              </div>
                            )}
                            {project.notes && (
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">
                                  Notes
                                </label>
                                <p className="mt-1 text-sm text-gray-900">{project.notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg border border-dashed border-purple-200 p-8 text-center">
                      <MapPinIcon className="w-10 h-10 text-purple-300 mx-auto mb-3" />
                      <p className="text-gray-500">No projects found</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Proposals Tab */}
            {activeTab === 'proposals' && (
              <div className="space-y-6">
                {/* Proposals Section */}
                <div className="bg-orange-50 rounded-lg p-6">
                  <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
                    <div className="flex items-center gap-2">
                      <FileTextIcon className="w-6 h-6 text-orange-600" />
                      <h2 className="text-xl font-semibold text-gray-900">
                        Proposals ({proposals.length})
                      </h2>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const returnTo = encodeURIComponent(`/leads/view/${id}?tab=proposals`)
                          navigate(`/proposal-requests/create?leadId=${id}&returnTo=${returnTo}`)
                        }}
                        className="flex items-center gap-2 px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
                      >
                        <PlusIcon className="w-4 h-4" />
                        New Proposal Request
                      </button>
                      <button
                        onClick={() => {
                          const returnTo = encodeURIComponent(`/leads/view/${id}?tab=proposals`)
                          navigate(`/proposals/create?leadId=${id}&returnTo=${returnTo}`)
                        }}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                      >
                        <PlusIcon className="w-4 h-4" />
                        New Proposal
                      </button>
                    </div>
                  </div>

                  {proposals.length === 0 ? (
                    <div className="bg-white rounded-lg border border-dashed border-orange-200 p-8 text-center">
                      <FileTextIcon className="w-10 h-10 text-orange-300 mx-auto mb-3" />
                      <p className="text-gray-500">No proposals found</p>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg border border-orange-100 overflow-hidden shadow-sm">
                      <ul className="divide-y divide-gray-100 lg:hidden">
                        {proposals.map((proposal) => {
                          const rt = encodeURIComponent(`/leads/view/${id}?tab=proposals`)
                          return (
                            <li key={proposal.id} className="p-3 space-y-1.5">
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(`/proposals/view/${proposal.id}?returnTo=${rt}`)
                                }
                                className="text-sm font-semibold text-blue-600 text-left"
                              >
                                {proposal.title || 'Untitled'}
                              </button>
                              <p className="text-xs text-gray-500">{proposal.proposal_number}</p>
                              <p className="text-sm text-gray-900">
                                ${(proposal.total || proposal.total_amount || 0).toLocaleString()} ·{' '}
                                {new Date(proposal.created_at).toLocaleDateString()}
                              </p>
                              <div className="flex items-center justify-between gap-2">
                                <span
                                  className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                    proposal.status === 'approved'
                                      ? 'bg-green-100 text-green-800'
                                      : proposal.status === 'sent'
                                        ? 'bg-blue-100 text-blue-800'
                                        : proposal.status === 'rejected'
                                          ? 'bg-red-100 text-red-800'
                                          : proposal.status === 'expired'
                                            ? 'bg-gray-100 text-gray-800'
                                            : 'bg-yellow-100 text-yellow-800'
                                  }`}
                                >
                                  {proposal.status?.charAt(0).toUpperCase() + proposal.status?.slice(1)}
                                </span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    navigate(`/proposals/edit/${proposal.id}?returnTo=${rt}`)
                                  }
                                  className="text-sm text-gray-600"
                                >
                                  Edit
                                </button>
                              </div>
                            </li>
                          )
                        })}
                      </ul>
                      <div className="hidden lg:block overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-orange-50/80">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Title
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Amount
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Status
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Date
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {proposals.map((proposal) => (
                              <tr key={proposal.id} className="hover:bg-orange-50/30">
                              <td className="px-4 py-3">
                                <button
                                  onClick={() => {
                                    const returnTo = encodeURIComponent(`/leads/view/${id}?tab=proposals`)
                                    navigate(`/proposals/view/${proposal.id}?returnTo=${returnTo}`)
                                  }}
                                  className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                  {proposal.title || 'Untitled'}
                                </button>
                                <div className="text-xs text-gray-500">{proposal.proposal_number}</div>
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                ${(proposal.total || proposal.total_amount || 0).toLocaleString()}
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                    proposal.status === 'approved'
                                      ? 'bg-green-100 text-green-800'
                                      : proposal.status === 'sent'
                                      ? 'bg-blue-100 text-blue-800'
                                      : proposal.status === 'rejected'
                                      ? 'bg-red-100 text-red-800'
                                      : proposal.status === 'expired'
                                      ? 'bg-gray-100 text-gray-800'
                                      : 'bg-yellow-100 text-yellow-800'
                                  }`}
                                >
                                  {proposal.status?.charAt(0).toUpperCase() + proposal.status?.slice(1)}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {new Date(proposal.created_at).toLocaleDateString()}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => {
                                      const returnTo = encodeURIComponent(`/leads/view/${id}?tab=proposals`)
                                      navigate(`/proposals/view/${proposal.id}?returnTo=${returnTo}`)
                                    }}
                                    className="text-blue-600 hover:text-blue-800 text-sm"
                                  >
                                    View
                                  </button>
                                  <button
                                    onClick={() => {
                                      const returnTo = encodeURIComponent(`/leads/view/${id}?tab=proposals`)
                                      navigate(`/proposals/edit/${proposal.id}?returnTo=${returnTo}`)
                                    }}
                                    className="text-gray-600 hover:text-gray-800 text-sm"
                                  >
                                    Edit
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    </div>
                  )}
                </div>

                {/* Proposal Requests Section */}
                <div className="bg-indigo-50 rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <ClipboardIcon className="w-6 h-6 text-indigo-600" />
                    <h2 className="text-xl font-semibold text-gray-900">
                      Proposal Requests ({proposalRequests.length})
                    </h2>
                  </div>

                  {proposalRequests.length === 0 ? (
                    <div className="bg-white rounded-lg border border-dashed border-indigo-200 p-8 text-center">
                      <ClipboardIcon className="w-10 h-10 text-indigo-300 mx-auto mb-3" />
                      <p className="text-gray-500">No proposal requests found</p>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg border border-indigo-100 overflow-hidden shadow-sm">
                      <ul className="divide-y divide-gray-100 lg:hidden">
                        {proposalRequests.map((request) => {
                          const rt = encodeURIComponent(`/leads/view/${id}?tab=proposals`)
                          return (
                            <li key={request.id} className="p-3 space-y-1.5">
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(`/proposal-requests/view/${request.id}?returnTo=${rt}`)
                                }
                                className="text-sm font-semibold text-blue-600 text-left"
                              >
                                {request.title || 'Untitled Request'}
                              </button>
                              <p className="text-xs text-gray-500">
                                {request.request_number || request.proposal_number}
                              </p>
                              <p className="text-sm text-gray-900">
                                {request.estimated_budget
                                  ? `$${request.estimated_budget.toLocaleString()}`
                                  : 'N/A'}{' '}
                                · {new Date(request.created_at).toLocaleDateString()}
                              </p>
                              <div className="flex items-center justify-between gap-2 flex-wrap">
                                <span
                                  className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                    request.status === 'converted'
                                      ? 'bg-green-100 text-green-800'
                                      : request.status === 'submitted'
                                        ? 'bg-blue-100 text-blue-800'
                                        : request.status === 'reviewing' ||
                                            request.status === 'in_progress'
                                          ? 'bg-yellow-100 text-yellow-800'
                                          : request.status === 'declined'
                                            ? 'bg-red-100 text-red-800'
                                            : 'bg-gray-100 text-gray-800'
                                  }`}
                                >
                                  {request.status?.charAt(0).toUpperCase() +
                                    request.status?.slice(1) || 'Submitted'}
                                </span>
                                <div className="flex gap-3 text-sm">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      navigate(`/proposal-requests/edit/${request.id}?returnTo=${rt}`)
                                    }
                                    className="text-gray-600"
                                  >
                                    Edit
                                  </button>
                                  {request.status !== 'converted' && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        navigate(
                                          `/proposals/create?requestId=${request.id}&returnTo=${rt}`
                                        )
                                      }
                                      className="text-green-600"
                                    >
                                      Create Proposal
                                    </button>
                                  )}
                                </div>
                              </div>
                            </li>
                          )
                        })}
                      </ul>
                      <div className="hidden lg:block overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-indigo-50/80">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Title
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Amount
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Status
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Date
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {proposalRequests.map((request) => (
                              <tr key={request.id} className="hover:bg-indigo-50/30">
                              <td className="px-4 py-3">
                                <button
                                  onClick={() => {
                                    const returnTo = encodeURIComponent(`/leads/view/${id}?tab=proposals`)
                                    navigate(`/proposal-requests/view/${request.id}?returnTo=${returnTo}`)
                                  }}
                                  className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                  {request.title || 'Untitled Request'}
                                </button>
                                <div className="text-xs text-gray-500">{request.request_number || request.proposal_number}</div>
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                {request.estimated_budget ? `$${request.estimated_budget.toLocaleString()}` : 'N/A'}
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                    request.status === 'converted'
                                      ? 'bg-green-100 text-green-800'
                                      : request.status === 'submitted'
                                      ? 'bg-blue-100 text-blue-800'
                                      : request.status === 'reviewing' || request.status === 'in_progress'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : request.status === 'declined'
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}
                                >
                                  {request.status?.charAt(0).toUpperCase() + request.status?.slice(1) || 'Submitted'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {new Date(request.created_at).toLocaleDateString()}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => {
                                      const returnTo = encodeURIComponent(`/leads/view/${id}?tab=proposals`)
                                      navigate(`/proposal-requests/view/${request.id}?returnTo=${returnTo}`)
                                    }}
                                    className="text-blue-600 hover:text-blue-800 text-sm"
                                  >
                                    View
                                  </button>
                                  <button
                                    onClick={() => {
                                      const returnTo = encodeURIComponent(`/leads/view/${id}?tab=proposals`)
                                      navigate(`/proposal-requests/edit/${request.id}?returnTo=${returnTo}`)
                                    }}
                                    className="text-gray-600 hover:text-gray-800 text-sm"
                                  >
                                    Edit
                                  </button>
                                  {request.status !== 'converted' && (
                                    <button
                                      onClick={() => {
                                        const returnTo = encodeURIComponent(`/leads/view/${id}?tab=proposals`)
                                        navigate(`/proposals/create?requestId=${request.id}&returnTo=${returnTo}`)
                                      }}
                                      className="text-green-600 hover:text-green-800 text-sm"
                                      title="Create Proposal"
                                    >
                                      Create Proposal
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'notes' && (
              <NotesAndActivityPanel
                notes={notes.map((n) => ({
                  id: n.id,
                  note: n.note,
                  created_at: n.created_at,
                  created_by_name: n.created_by_name,
                }))}
                activities={activities}
                notesTitle="Lead Notes"
                newNote={newNote}
                onNewNoteChange={setNewNote}
                onAddNote={handleAddNote}
                savingNote={savingNote}
              />
            )}
          </div>
      </div>

      <div className="native-action-bar flex items-center justify-between gap-3 max-w-6xl mx-auto lg:mt-4 lg:rounded-xl lg:border lg:border-gray-200">
        <button type="button" onClick={handleBack} className={mobile.secondaryBtn}>
          {backLabel}
        </button>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setActionsOpen(true)} className={mobile.secondaryBtn}>
            <MoreHorizontalIcon className="w-4 h-4" />
            Actions
          </button>
          {!lead.converted_to_customer_id && (
            <button
              type="button"
              onClick={() => setShowConvertModal(true)}
              className={mobile.successBtn}
            >
              <UserCheckIcon className="w-4 h-4" />
              Convert to Customer
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate(`/leads/edit/${id}`)}
            className={mobile.primaryBtn}
          >
            <EditIcon className="w-4 h-4" />
            Edit Lead
          </button>
        </div>
      </div>

      <MobileSheet open={actionsOpen} onClose={() => setActionsOpen(false)} title="Lead actions">
        <div className="space-y-2">
          {!lead.converted_to_customer_id && (
            <button
              type="button"
              onClick={() => {
                setActionsOpen(false)
                setShowConvertModal(true)
              }}
              className={`${mobile.successBtn} w-full`}
            >
              <UserCheckIcon className="w-4 h-4" />
              Convert to Customer
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setActionsOpen(false)
              const rt = encodeURIComponent(`/leads/view/${id}?tab=proposals`)
              navigate(`/proposal-requests/create?leadId=${id}&returnTo=${rt}`)
            }}
            className={`${mobile.secondaryBtn} w-full`}
          >
            <ClipboardIcon className="w-4 h-4" />
            New Proposal Request
          </button>
          <button
            type="button"
            onClick={() => {
              setActionsOpen(false)
              const rt = encodeURIComponent(`/leads/view/${id}?tab=proposals`)
              navigate(`/proposals/create?leadId=${id}&returnTo=${rt}`)
            }}
            className={`${mobile.secondaryBtn} w-full`}
          >
            <FileTextIcon className="w-4 h-4" />
            New Proposal
          </button>
          <button
            type="button"
            onClick={() => {
              setActionsOpen(false)
              navigate(`/leads/edit/${id}`)
            }}
            className={`${mobile.primaryBtn} w-full`}
          >
            <EditIcon className="w-4 h-4" />
            Edit Lead
          </button>
        </div>
      </MobileSheet>

      <ConvertLeadModal
        lead={lead}
        open={showConvertModal && !!lead}
        onClose={() => setShowConvertModal(false)}
        onSuccess={async (customerId) => {
          setShowConvertModal(false)
          setNotification({
            message: 'Lead converted to customer successfully',
            type: 'success',
            customerId,
          })
          setTimeout(() => setNotification(null), 8000)
        }}
        onError={(message) => {
          setNotification({ message, type: 'error' })
          setTimeout(() => setNotification(null), 8000)
        }}
        serviceCategories={serviceCategories}
      />

      {notification && (
        <div
          className={`fixed top-4 right-4 z-[60] px-4 py-3 rounded-lg shadow-lg max-w-md ${
            notification.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span>{notification.message}</span>
            {notification.customerId && (
              <button
                onClick={() => navigate(`/customers/view/${notification.customerId}`)}
                className="underline font-medium"
              >
                View Customer
              </button>
            )}
            <button onClick={() => setNotification(null)} className="ml-auto hover:opacity-75">
              ✕
            </button>
          </div>
        </div>
      )}
    </MobilePage>
  )
}

